import { dateSuisse } from '../lib/dates';
import { estPartiel, quantiteLisible } from '../lib/bons-achat';
import { tr, trf } from '../lib/i18n';
import { pileDePolice } from '../lib/polices';
import { C } from '../theme/palette';
import { PleinEcran } from './ui/PleinEcran.jsx';

const boite = {
  border: '1px solid #ddd',
  borderRadius: 6,
  padding: '8px 10px',
  lineHeight: 1.5,
  fontSize: 11,
};
const etiquette = {
  display: 'block',
  fontSize: 8.5,
  letterSpacing: 0.6,
  color: '#666',
  textTransform: 'uppercase',
  fontWeight: 700,
  marginBottom: 2,
};
const bouton = (fond, texte = '#fff') => ({
  background: fond,
  color: texte,
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
});

/** La signature dessinée à la main, ou la place pour signer au stylo. */
function Paraphe({ image }) {
  return /^data:image\//.test(String(image || '')) ? (
    <img src={image} alt="" style={{ height: 38, maxWidth: '100%', display: 'block' }} />
  ) : (
    <div style={{ height: 38 }} />
  );
}

/**
 * Le bon d'achat de matériaux sur une feuille A4, à imprimer ou à montrer au
 * comptoir du fournisseur.
 *
 * <p>Sans prix : le fournisseur sait quoi donner, à qui, pour quel chantier,
 * et à qui adresser sa facture (avec le numéro du bon). Le bureau l'imprime ;
 * l'employé l'ouvre sur son téléphone (« Montrer le bon »).
 *
 * @param {object} props propriétés
 * @param {object} props.bon bon d'achat, tel que le serveur le rend
 * @param {object} [props.soc] société : en-tête du bon
 * @param {() => void} props.onClose ferme la feuille
 * @returns {import('react').ReactElement} la feuille
 */
export function BonAchatA4({ bon, soc, onClose }) {
  const imprime = () => {
    try {
      window.print();
    } catch {
      /* l'impression n'est pas offerte : la feuille reste à l'écran */
    }
  };
  const nomSoc = soc?.nom || '';
  const coord = [soc?.adr, soc?.npa].filter(Boolean).join(' · ');
  const contact = [soc?.tel, soc?.email].filter(Boolean).join(' · ');
  return (
    <PleinEcran>
      <div
        role="dialog"
        aria-label={tr('Bon d’achat de matériaux')}
        style={{
          position: 'fixed',
          inset: 0,
          paddingTop: 'var(--st-haut)',
          zIndex: 9999,
          background: '#5b5f66',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <style>
          {
            '@media print{@page{size:A4;margin:14mm} body{visibility:hidden!important} #bonAchatA4,#bonAchatA4 *{visibility:visible!important} #bonAchatA4{position:fixed;left:0;top:0;width:100%;box-shadow:none!important} .bonNoPrint{display:none!important}}'
          }
        </style>
        <div
          className="bonNoPrint"
          style={{
            background: C.navy,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{ ...bouton('none'), fontSize: 15, padding: '6px 4px' }}
          >
            ‹ {tr('Retour')}
          </button>
          <button type="button" onClick={imprime} style={bouton(C.green)}>
            🖨 {tr('Imprimer / PDF')}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 10px' }}>
          <div
            id="bonAchatA4"
            style={{
              maxWidth: 680,
              margin: '0 auto',
              background: '#fff',
              borderRadius: 4,
              padding: '28px 30px',
              fontFamily: pileDePolice(soc?.docFont) || 'inherit',
              color: '#111',
              boxShadow: '0 6px 24px rgba(0,0,0,.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{nomSoc}</div>
                <div style={{ fontSize: 10, color: '#555', lineHeight: 1.5 }}>
                  {coord}
                  {coord && contact ? <br /> : null}
                  {contact}
                </div>
              </div>
              {soc?.logo ? (
                <img src={soc.logo} alt="" style={{ maxHeight: 50, maxWidth: 140 }} />
              ) : null}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, margin: '18px 0 2px' }}>
              {bon.chantierNom ? tr('Bon d’achat de matériaux') : tr('Bon d’achat')}
            </div>
            <div style={{ fontSize: 11, color: '#555' }}>
              N° {bon.numero}
              {bon.dateBon ? ' · ' + dateSuisse(bon.dateBon) : ''}
              {/* Le matériel du chantier se suit sous le numéro de son offre. */}
              {bon.offreNumero ? ' · ' + trf('Offre n° {0}', bon.offreNumero) : ''}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
                marginTop: 12,
              }}
            >
              <div style={boite}>
                <span style={etiquette}>{tr('Fournisseur')}</span>
                {bon.fournisseur}
                {bon.fournisseurAdresse ? (
                  <>
                    <br />
                    {bon.fournisseurAdresse}
                  </>
                ) : null}
              </div>
              <div style={boite}>
                <span style={etiquette}>{tr('Achat fait par')}</span>
                {bon.employeNom || '—'}
                {bon.employeTelephone ? (
                  <>
                    <br />
                    {tr('Tél.')} {bon.employeTelephone}
                  </>
                ) : null}
              </div>
              <div style={{ ...boite, gridColumn: '1 / -1' }}>
                {/* Au magasin, le bon remplit le stock : pas de chantier. */}
                <span style={etiquette}>{bon.chantierNom ? tr('Chantier') : tr('Pour')}</span>
                {bon.chantierNom || tr('Le stock du magasin')}
                {bon.chantierAdresse ? ' · ' + bon.chantierAdresse : ''}
              </div>
            </div>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 11 }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      background: C.navy,
                      color: '#fff',
                      textAlign: 'left',
                      padding: '6px 7px',
                    }}
                  >
                    {bon.chantierNom ? tr('Matériau') : tr('Article')}
                  </th>
                  <th
                    style={{
                      background: C.navy,
                      color: '#fff',
                      textAlign: 'right',
                      padding: '6px 7px',
                      width: 110,
                    }}
                  >
                    {tr('Quantité')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(bon.lignes || []).map((l, i) => (
                  <tr key={i} style={l.etat === 'MANQUANT' ? { color: C.red } : undefined}>
                    <td style={{ padding: 7, borderBottom: '1px solid #e5e5e5' }}>
                      {l.etat === 'MANQUANT' ? '✗ ' : l.etat === 'PRIS' ? '✓ ' : ''}
                      <span
                        style={
                          l.etat === 'MANQUANT' ? { textDecoration: 'line-through' } : undefined
                        }
                      >
                        {l.designation}
                      </span>
                      {l.etat === 'MANQUANT' ? ' — ' + tr('manquant chez le fournisseur') : ''}
                      {estPartiel(l) ? ' — ' + tr('pris en partie, le reste manquait') : ''}
                    </td>
                    <td
                      style={{ padding: 7, borderBottom: '1px solid #e5e5e5', textAlign: 'right' }}
                    >
                      {quantiteLisible(l)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bon.remarque ? (
              <div style={{ ...boite, marginTop: 12, whiteSpace: 'pre-wrap' }}>
                <span style={etiquette}>{tr('Remarque')}</span>
                {bon.remarque}
              </div>
            ) : null}
            <div
              style={{
                border: '2px solid ' + C.navy,
                borderRadius: 6,
                padding: 10,
                marginTop: 14,
                lineHeight: 1.55,
                background: '#F6F8FB',
                fontSize: 11,
              }}
            >
              {tr('Merci d’adresser la facture à')} <b>{nomSoc}</b>, {tr('avec la mention')}{' '}
              <b>
                « {bon.numero}
                {bon.offreNumero ? ' — ' + trf('Offre n° {0}', bon.offreNumero) : ''}
                {bon.chantierNom ? ' — ' + bon.chantierNom : ''} »
              </b>
              .
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 26,
                marginTop: 30,
                fontSize: 10.5,
                color: '#555',
              }}
            >
              {/* À gauche le bureau qui envoie, à droite l'employé qui récupère
                la marchandise : le fournisseur n'a rien à signer. */}
              <div>
                <span style={etiquette}>{tr('Établi par')}</span>
                <div style={{ color: '#111' }}>{bon.etabliPar || ''}</div>
                <Paraphe image={bon.signatureEtabli} />
                <div style={{ borderTop: '1px solid #999', paddingTop: 4 }}>{tr('Signature')}</div>
              </div>
              <div>
                <span style={etiquette}>{tr('Matériel récupéré par')}</span>
                <div style={{ color: '#111' }}>{bon.employeNom || ''}</div>
                <Paraphe image={bon.signatureEmploye} />
                <div style={{ borderTop: '1px solid #999', paddingTop: 4 }}>
                  {tr('Date et signature')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PleinEcran>
  );
}
